import { useCallback, useEffect, useMemo, useState } from 'react'
import { Descendant } from 'slate'

import { VariableTextFeatureFlag } from '@makyoapp/makyo-common'

import { IAppLayer } from '@/core/app-builder/app/layer'
import { globalVariableClient } from '@/core/app-builder/global-variables'
import { parseVariableText } from '@/core/app-builder/parser/variable-text'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useCompContext } from '@/context/__project__/CompContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { collectTableIds } from '@/shared/collect-table-ids'

import { useTableSubscription } from './useTableSubscription'

function findGlobalVariableFlagsInDescendant(node: Descendant | Descendant[]): string[] {
  let tableIds: string[] = []

  if (Array.isArray(node)) {
    // Process array of nodes
    for (const n of node) {
      if (typeof n === 'object') {
        // Check for tbl property at this level
        if ('type' in n && n.type === VariableTextFeatureFlag.GlobalVariable) {
          tableIds.push(n.type)
        }
        if ('children' in n) {
          // Recursively process children
          tableIds.push(...findGlobalVariableFlagsInDescendant(n.children))
        }
      }
    }
  } else {
    if (node && typeof node === 'object') {
      // Process single node
      if ('type' in node && node.type === VariableTextFeatureFlag.GlobalVariable) {
        tableIds.push(node.type)
      }
      if ('children' in node) {
        tableIds.push(...findGlobalVariableFlagsInDescendant(node.children))
      }
    }
  }

  return [...new Set(tableIds)]
}

/**
 * New Variable Text Parser Hook
 */
export function useParseVariableText(
  node: Descendant[] = [],
  client?: IAppLayer,
  tblId?: string,
  rowIdx?: number,
  rowId?: string
) {
  const { dbClient, language, userSpecific } = useDbClientContext()
  const { userEmail } = useAppAuthContext()

  const { detailTableId, rowId: detailRowId } = useCompContext()

  const [value, setValue] = useState('')

  // find all table ids inside the node
  const tableIds = useMemo(() => {
    const tableIdsMap = collectTableIds(node, dbClient)
    if (detailTableId) {
      tableIdsMap.inject(detailTableId, 'detailTableId')
    }
    return Array.from(tableIdsMap.keys())
  }, [node, detailTableId, dbClient])

  // parse variable text fn
  const handleParseVariableText = useCallback(async () => {
    if (!dbClient) return
    setValue(parseVariableText(node, dbClient, client, userEmail, language, userSpecific, detailTableId, detailRowId))
  }, [client, dbClient, detailRowId, detailTableId, language, node, userEmail, userSpecific])

  // subscribe to table changes
  useTableSubscription({
    dbClient,
    tableIds,
    callback: handleParseVariableText,
  })

  // subscribe to page (layer) updates so variable text recomputes when page content changes
  useEffect(() => {
    if (!client) return
    const unsubscribe = client.subscribe(() => {
      handleParseVariableText()
    })
    return () => {
      unsubscribe()
    }
  }, [client, handleParseVariableText])

  const hasGlobalVariableFlag = findGlobalVariableFlagsInDescendant(node).length > 0

  // subscribe to global variables changes
  useEffect(() => {
    if (!globalVariableClient || !hasGlobalVariableFlag) return

    const unsubscribe = globalVariableClient.subscribe(() => {
      handleParseVariableText()
    })

    return () => {
      unsubscribe()
    }
  }, [handleParseVariableText, hasGlobalVariableFlag])

  return value
}
